// facts/preflopAssemble.ts - the BOARDLESS preflop facts path (PF3). The preflop sibling of assemble.ts.
//
// assemble.ts (facts/assemble.ts) requires a board - it derives a textureClass and stores board.cards. A
// preflop decision has no board, so this module assembles a PreflopEngineFacts (no board field) and
// projects it to a PreflopFactsDigest that is BOARDLESS + card-free BY CONSTRUCTION (the digest type has
// no board / texture / card / dollar field). The firewall guard is the SAME one the postflop digest uses
// (assertSerializedSafe, digest.ts) - one guard for both projections, so a preflop label that accidentally
// embeds a card token still fails loudly instead of reaching the coach.
//
// The preflop-specific coach fields (BUILD_PLAN §PF3): chart region, positional class, effective-stack
// bucket, pot type. Everything upstream has already run - deriveHeroPreflopSpot produced the facing line,
// recommendPreflop resolved hero's combo against the shipped charts; this stitches those into the one
// validated record + its coach projection, and computes the effective-stack bucket. No I/O, no re-resolve.
import { PreflopEngineFactsSchema, PreflopFactsDigestSchema, } from '@postflop/schema';
import { assertSerializedSafe } from './digest';
/** Effective stack in bb → the coarse depth bucket the coach reads (nearest shipped family). */
export function stackDepthBucketOf(effectiveStackBB) {
    if (effectiveStackBB <= 15)
        return '10bb';
    if (effectiveStackBB <= 30)
        return '20bb';
    if (effectiveStackBB <= 60)
        return '40bb';
    if (effectiveStackBB <= 115)
        return '80bb';
    return '150bb';
}
/** Effective stack in bb → the sprBucket-as-depth proxy the shipped preflop RULE layer keys on (mirrors
 *  strategy/preflop.ts's local `stackDepthBucket`, the ICM short-stack rule convention). Kept in lock-step
 *  with that mapping so the digest's depthProxyBucket matches the bucket the rules actually fired under. */
export function preflopDepthProxyOf(effectiveStackBB) {
    if (effectiveStackBB <= 12)
        return 'under1';
    if (effectiveStackBB <= 25)
        return '1to3';
    if (effectiveStackBB <= 45)
        return '3to6';
    if (effectiveStackBB <= 80)
        return '6to13';
    return 'over13';
}
/**
 * Assemble one preflop decision's full PreflopEngineFacts. Derives the effective-stack bucket + depth proxy
 * and carries the chart region / family / inChart from the recommendation. Validates against the schema at
 * the boundary (repo posture: zod at every serialization boundary) and returns the parsed record.
 */
export function assemblePreflopFacts(input) {
    const { math, recommendation } = input;
    const facts = {
        v: 1,
        handId: input.handId,
        street: 'preflop',
        decisionIndex: input.decisionIndex,
        context: {
            gameType: input.context.gameType,
            mode: input.context.mode,
            position: input.context.position,
            positionClass: input.context.positionClass,
            playersInHand: input.context.playersInHand,
            icmPressure: input.context.icmPressure,
        },
        hero: { holeCards: input.hero.holeCards },
        preflop: {
            role: math.role,
            potType: math.potType,
            effectiveStackBB: math.effectiveStackBB,
            stackDepthBucket: stackDepthBucketOf(math.effectiveStackBB),
            depthProxyBucket: preflopDepthProxyOf(math.effectiveStackBB),
            toCallBB: math.toCallBB,
            potBB: math.potBB,
            openerPosition: math.openerPosition,
            chartRegion: recommendation.chartKey,
            family: recommendation.family,
            inChart: recommendation.inChart,
        },
        villain: {
            seat: input.villain.seat,
            archetypeId: input.villain.archetype.id,
            imbalances: input.villain.archetype.imbalances,
            rangeShape: input.villain.rangeShape,
        },
        recommendation,
        userAction: input.userAction,
    };
    return PreflopEngineFactsSchema.parse(facts);
}
/** Throw if the preflop digest's serialization leaks a raw card string or a `$` amount - the SAME firewall
 *  guard the postflop digest runs (digest.ts). The preflop digest is boardless + card-free by construction;
 *  this bites only on an authored free-form label (a rule principle, a rangeShape topClass) that embeds a
 *  card token. Usable standalone on any PreflopFactsDigest before it crosses to the coach. */
export function assertPreflopDigestSafe(digest) {
    assertSerializedSafe(digest, 'PreflopFactsDigest');
}
/**
 * Project the full PreflopEngineFacts to the coach-facing PreflopFactsDigest. Reads ONLY bucketed /
 * enumerated / label fields - never hero.holeCards, never any board (there is none, and no field for one).
 * Runs assertPreflopDigestSafe before returning so a poisoned authored label cannot reach the coach.
 */
export function projectPreflopDigest(facts) {
    const digest = {
        v: 1,
        street: 'preflop',
        context: {
            gameType: facts.context.gameType,
            mode: facts.context.mode,
            positionClass: facts.context.positionClass,
            position: facts.context.position,
            playersInHand: facts.context.playersInHand,
            icmPressure: facts.context.icmPressure,
        },
        role: facts.preflop.role,
        potType: facts.preflop.potType,
        stackDepthBucket: facts.preflop.stackDepthBucket,
        chartRegion: facts.preflop.chartRegion,
        inChart: facts.preflop.inChart,
        openerPosition: facts.preflop.openerPosition,
        villain: {
            archetypeId: facts.villain.archetypeId,
            imbalances: facts.villain.imbalances,
            rangeShape: facts.villain.rangeShape,
        },
        recommendation: {
            intent: facts.recommendation.finalIntent,
            action: facts.recommendation.action,
            sizingLabel: facts.recommendation.sizing?.label,
            // SUPPRESSED RULES ARE DROPPED HERE, AND THAT IS THE WHOLE POINT OF THE FILTER.
            // A suppressed rule MATCHED but the resolver refused it (schema FiredRuleSchema.suppressed;
            // strategy/preflop.ts ruleShapedAnswer) - e.g. OVERCALLER_ISO_LIMPER matches the SPOT (limped pot,
            // over-calling villain) and never hero's cards, so it says "isolate the limpers with a bigger raise"
            // over a hand the chart folds. Its principle argues for an action the engine did NOT take. Handing
            // that to the coach alongside `intent: 'fold'` is handing it a contradiction and asking it to write
            // prose from both halves - which is exactly the contradiction the deterministic preflop coach shipped
            // before f7be1f1, in the LLM's own words and at a real per-call cost.
            // The rules stay in `facts.recommendation.firedRules` - that they matched is real provenance the
            // yardstick (yardstick/run.ts) and dimension attribution (grading/dimensions.ts) assert on. This is
            // the COACH-facing projection, and the coach may only be told what the engine actually stands behind.
            firedRules: facts.recommendation.firedRules
                .filter((r) => !r.suppressed)
                .map((r) => ({
                id: r.id,
                principle: r.principle,
                confidence: r.confidence,
            })),
            alternatives: facts.recommendation.alternatives.map((a) => ({ intent: a.intent })),
        },
        userAction: facts.userAction
            ? {
                intent: facts.userAction.intent,
                action: facts.userAction.action,
                gradeBand: facts.userAction.gradeBand,
            }
            : undefined,
    };
    // Validate the projection against the schema (catches any drift), then enforce the firewall.
    const parsed = PreflopFactsDigestSchema.parse(digest);
    assertPreflopDigestSafe(parsed);
    return parsed;
}
//# sourceMappingURL=preflopAssemble.js.map